import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiChevronDown, FiSettings, FiLogOut } from 'react-icons/fi';
import { collection, doc, getDoc, onSnapshot } from 'firebase/firestore';
import { auth, db } from '../firebaseConfig'; // Adjust path if needed
import '../styles/StrategyRoom.css';
import NotificationBell from './Global/notification/NotificationBell';
import { getCurrentSeason } from '../data/seasons';
import SeasonConfigModal from './SeasonConfigModal';
import SeasonSwitcher from './SeasonSwitcher';
import { listSeasons } from '../utils/seasonApi';
import { getSeasonSelectionPayload, setSeasonSelection } from '../utils/seasonSelection';

const normalizeSeasonRow = (row) => ({
  ...row,
  id: row?.id != null ? String(row.id) : null,
  year: row?.year != null ? String(row.year) : (row?.id != null ? String(row.id) : null),
  displayName: row?.displayName || row?.name || (row?.year != null ? String(row.year) : String(row?.id || '')),
  status: (row?.status || '').toLowerCase(),
});

const TopNavBar = ({ userProfile, teamLogo, setUserProfile, setTeamLogo }) => {
  const navigate = useNavigate();
  const [showDropdown, setShowDropdown] = useState(false);
  const [teamId, setTeamId] = useState(null);
  const [teamName, setTeamName] = useState(null);
  const [localTeamLogo, setLocalTeamLogo] = useState(null);
  const [isLogoLoading, setIsLogoLoading] = useState(true);
  const [scrims, setScrims] = useState([]);
  const [now, setNow] = useState(new Date());
  const [showSeasonConfigModal, setShowSeasonConfigModal] = useState(false);
  const [baseTeamName, setBaseTeamName] = useState(null);
  const [baseTeamLogo, setBaseTeamLogo] = useState(null);
  const [activeSeasonId, setActiveSeasonId] = useState(null);
  const [activeSeasonYear, setActiveSeasonYear] = useState(null);
  const [availableSeasons, setAvailableSeasons] = useState([]);
  const [teamDataState, setTeamDataState] = useState(null);
  const activeSeasonKey = activeSeasonYear || activeSeasonId || getCurrentSeason();

  const loadSeasonData = useCallback(async (teamIdValue, seasonSelection, teamData) => {
    const seasonId = seasonSelection?.seasonId ? String(seasonSelection.seasonId) : null;
    const seasonYear = seasonSelection?.seasonYear ? String(seasonSelection.seasonYear) : null;
    const candidates = [...new Set([seasonYear, seasonId].filter(Boolean))];

    for (const seasonKey of candidates) {
      const seasonRef = doc(db, `teams/${teamIdValue}/seasons/${seasonKey}`);
      const seasonSnap = await getDoc(seasonRef);
      if (!seasonSnap.exists()) continue;
      const season = seasonSnap.data();
      setTeamName(season.displayName || teamData?.name || teamIdValue);
      setLocalTeamLogo(season.logo || teamData?.logo || null);
      setTeamLogo(season.logo || teamData?.logo || null);
      setShowSeasonConfigModal(false);
      return;
    }

    setShowSeasonConfigModal(true);
    setTeamName(teamData?.name || teamIdValue);
    setLocalTeamLogo(teamData?.logo || null);
    setTeamLogo(teamData?.logo || null);
  }, [setTeamLogo]);

  const refreshSeasonList = useCallback(async (teamIdValue) => {
    try {
      const rows = await listSeasons(teamIdValue);
      const normalized = rows.map(normalizeSeasonRow);
      setAvailableSeasons(normalized);
      return normalized;
    } catch (err) {
      console.error('Failed to list seasons', err);
      return [];
    }
  }, []);

  const resolveSeasonContext = useCallback((value, seasons) => {
    const rows = (seasons || []).map(normalizeSeasonRow);
    const raw = value && typeof value === 'object' ? value : { seasonId: value, seasonYear: value };
    const seasonId = raw?.seasonId != null ? String(raw.seasonId) : null;
    const seasonYear = raw?.seasonYear != null ? String(raw.seasonYear) : null;
    const match = rows.find((row) =>
      (seasonId && (row.id === seasonId || row.year === seasonId)) ||
      (seasonYear && (row.id === seasonYear || row.year === seasonYear))
    );
    if (match) {
      return { seasonId: String(match.id), seasonYear: String(match.year || match.id) };
    }
    if (seasonId || seasonYear) {
      const fallback = String(seasonYear || seasonId);
      return { seasonId: String(seasonId || fallback), seasonYear: fallback };
    }
    return null;
  }, []);

  const toDate = (value) => {
    if (!value) return null;
    if (value.toDate) return value.toDate();
    if (value instanceof Date) return value;
    return null;
  };

  const buildLocalDateTime = (dateStr, timeStr) => {
    if (!dateStr) return null;
    const [year, month, day] = dateStr.split('-').map((val) => parseInt(val, 10));
    const [hour, minute] = (timeStr || '00:00').split(':').map((val) => parseInt(val, 10));
    if (!year || !month || !day) return null;
    return new Date(year, month - 1, day, hour || 0, minute || 0);
  };

  const getScrimStart = (scrim) => {
    if (!scrim) return null;
    if (scrim.allDay) {
      return buildLocalDateTime(scrim.startDate, '00:00');
    }
    return (
      toDate(scrim.startAt) ||
      buildLocalDateTime(scrim.startDate, scrim.startTime || '00:00')
    );
  };

  const formatTime = (date) =>
    new Intl.DateTimeFormat(undefined, {
      hour: 'numeric',
      minute: '2-digit',
    }).format(date);

  const notificationItems = useMemo(() => {
    const list = [];
    scrims.forEach((scrim) => {
      if (scrim.allDay) return;
      const start = getScrimStart(scrim);
      if (!start) return;
      const diffMin = Math.round((start.getTime() - now.getTime()) / 60000);
      if (diffMin < 0 || diffMin > 60) return;
      const tier = diffMin <= 15 ? 15 : 60;
      list.push({
        id: `${scrim.id}-${tier}`,
        tier,
        title: scrim.title || 'Untitled event',
        description: `Scrim reminder - starts in ${diffMin} min.`,
        meta: `Starts at ${formatTime(start)}`,
        scrimId: scrim.id,
      });
    });
    return list.sort((a, b) => a.tier - b.tier);
  }, [scrims, now]);

  const handleNotificationClick = () => {
    navigate('/my-calendar');
  };

  const handleSeasonChange = async (value) => {
    if (!teamId || !value) return;
    const resolved = resolveSeasonContext(value, availableSeasons);
    if (!resolved) return;
    setActiveSeasonId(resolved.seasonId);
    setActiveSeasonYear(resolved.seasonYear);
    setSeasonSelection(teamId, resolved);
    const fallbackTeam = teamDataState || { name: baseTeamName, logo: baseTeamLogo };
    await loadSeasonData(teamId, resolved, fallbackTeam);
  };

  useEffect(() => {
    const fetchUserProfile = async () => {
      const user = auth?.currentUser;
      if (!user) {
        setIsLogoLoading(false);
        return;
      }

      setLocalTeamLogo(null);
      setIsLogoLoading(true);

      const userRef = doc(db, `users/${user.uid}`);
      const userSnap = await getDoc(userRef);
      if (!userSnap.exists()) {
        setIsLogoLoading(false);
        return;
      }

      const data = userSnap.data();
      setUserProfile(data);
      if (!data.teamId) {
        setIsLogoLoading(false);
        return;
      }

      setTeamId(data.teamId);
      const teamRef = doc(db, `teams/${data.teamId}`);
      const teamSnap = await getDoc(teamRef);
      if (!teamSnap.exists()) {
        setIsLogoLoading(false);
        return;
      }

      const teamData = teamSnap.data();
      setTeamDataState(teamData);
      setBaseTeamName(teamData.name || data.teamId);
      setBaseTeamLogo(teamData.logo || null);

      const seasons = await refreshSeasonList(data.teamId);
      const storedSelection = getSeasonSelectionPayload(data.teamId);

      const pickSeason = () => {
        const fromStored = resolveSeasonContext(storedSelection, seasons);
        if (fromStored) return fromStored;

        if (teamData.currentSeasonYear) {
          const targetYear = String(teamData.currentSeasonYear);
          const match = seasons.find((s) => String(s.year || s.id) === targetYear);
          if (match) {
            return { seasonId: String(match.id), seasonYear: String(match.year || match.id) };
          }
        }

        const firstActive = seasons.find((s) => (s.status || '').toLowerCase() === 'active');
        if (firstActive) {
          return {
            seasonId: String(firstActive.id),
            seasonYear: String(firstActive.year || firstActive.id),
          };
        }

        if (seasons.length) {
          return {
            seasonId: String(seasons[0].id),
            seasonYear: String(seasons[0].year || seasons[0].id),
          };
        }

        const current = String(getCurrentSeason());
        return { seasonId: current, seasonYear: current };
      };

      const chosenSeason = pickSeason();
      setActiveSeasonId(chosenSeason.seasonId);
      setActiveSeasonYear(chosenSeason.seasonYear);
      setSeasonSelection(data.teamId, chosenSeason);

      await loadSeasonData(data.teamId, chosenSeason, teamData);

      setIsLogoLoading(false);
    };

    fetchUserProfile();
  }, [loadSeasonData, refreshSeasonList, resolveSeasonContext, setUserProfile]);

  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!teamId) return;
    const seasonCandidates = [...new Set(
      [activeSeasonYear, activeSeasonId]
        .filter(Boolean)
        .map((value) => String(value))
    )];
    const seasonDataByKey = new Map();
    let legacyData = [];

    const mergeRows = () => {
      const seasonRows = seasonCandidates.flatMap((key) => seasonDataByKey.get(key) || []);
      const sourceRows = seasonRows.length ? seasonRows : legacyData;
      const deduped = [];
      const seen = new Set();
      sourceRows.forEach((row) => {
        const rowId = String(row.id || '');
        if (!rowId || seen.has(rowId)) return;
        seen.add(rowId);
        deduped.push(row);
      });
      setScrims(deduped);
    };

    const unsubscribers = [];

    seasonCandidates.forEach((seasonKey) => {
      const seasonRef = collection(db, 'teams', teamId, 'seasons', seasonKey, 'calendar');
      const unsub = onSnapshot(
        seasonRef,
        (snapshot) => {
          const data = snapshot.docs.map((docSnap) => ({
            id: docSnap.id,
            ...docSnap.data(),
          }));
          seasonDataByKey.set(seasonKey, data);
          mergeRows();
        },
        (err) => {
          console.error(`Failed to subscribe to season calendar (${seasonKey})`, err);
          seasonDataByKey.set(seasonKey, []);
          mergeRows();
        }
      );
      unsubscribers.push(unsub);
    });

    const legacyRef = collection(db, 'teams', teamId, 'calendar');
    const legacyUnsub = onSnapshot(
      legacyRef,
      (snapshot) => {
        legacyData = snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));
        mergeRows();
      },
      (err) => {
        console.error('Failed to subscribe to legacy calendar', err);
        legacyData = [];
        mergeRows();
      }
    );
    unsubscribers.push(legacyUnsub);

    return () => unsubscribers.forEach((unsub) => unsub && unsub());
  }, [teamId, activeSeasonId, activeSeasonYear]);


  

  return (
    <div className="top-bar">
      <div className="nav-left">
        <img src="/assets/theboxlogo.png" className="logo-img" alt="TheBox Logo" />
        <nav className="nav-links">
          <a data-tour="strategy-room" onClick={() => navigate('/my-strategy')}>Strategy</a>
          <a data-tour="playbook" onClick={() => navigate('/my-playbook')}>MyPlaybook</a>
          <a data-tour="calendar" onClick={() => navigate('/my-calendar')}>MyCalendar</a>
          <a data-tour="stats" onClick={() => navigate('/my-stats')}>MyStats</a>
          <a onClick={() => navigate('/community')}>Community</a>
        </nav>
      </div>
      
      <div className="nav-actions" style={{ position: 'relative' }}>
        {teamId && (
          <div style={{ marginRight: '12px' }}>
            <SeasonSwitcher
              teamId={teamId}
              activeSeason={activeSeasonKey || ''}
              onChange={handleSeasonChange}
              seasons={availableSeasons}
            />
          </div>
        )}
        <NotificationBell items={notificationItems} onItemClick={handleNotificationClick} />
        {userProfile && (
          <div
            className={`user-dropdown-trigger ${showDropdown ? 'open' : ''}`}
            onClick={() => setShowDropdown(prev => !prev)}
          >
            <img
              src={localTeamLogo || '/assets/theboxlogo.png'}
              alt="Team Logo"
              className="user-avatar"
              style={{ opacity: isLogoLoading ? 0 : 1, transition: 'opacity 0.2s' }}
            />
            <span className="user-name">{teamName || userProfile.ign || userProfile.firstName}</span>
            <FiChevronDown size={14} className="dropdown-arrow" />
          </div>
        )}

        {showDropdown && (
          <div className="user-dropdown-menu">
            <div className="dropdown-item" data-tour="settings" onClick={() => navigate('/settings')}>
              <FiSettings size={16} className="dropdown-icon" />
              <span>Settings</span>
            </div>
            <div
              className="dropdown-item"
              onClick={async () => {
                await auth.signOut();
                setUserProfile(null);
                setTeamLogo(null);
                setShowDropdown(false);
                navigate('/landing');
              }}
            >
              <FiLogOut size={16} className="dropdown-icon" />
              <span>Logout</span>
            </div>
          </div>
        )}
      </div>

      {/* Season Configuration Modal */}
      {showSeasonConfigModal && teamId && (
        <SeasonConfigModal
          teamId={teamId}
          baseTeamName={baseTeamName}
          baseTeamLogo={baseTeamLogo}
          onClose={() => {
            localStorage.removeItem(`seasonConfigDismissed_${activeSeasonKey}`);
            setShowSeasonConfigModal(false);
          }}
          onSave={(config) => {
            const seasonSelection = {
              seasonId: activeSeasonId || activeSeasonKey,
              seasonYear: activeSeasonYear || activeSeasonKey,
            };
            localStorage.removeItem(`seasonConfigDismissed_${activeSeasonKey}`);
            refreshSeasonList(teamId);
            loadSeasonData(teamId, seasonSelection, {
              name: baseTeamName,
              logo: baseTeamLogo,
            });
            setActiveSeasonId(seasonSelection.seasonId);
            setActiveSeasonYear(seasonSelection.seasonYear);
            setSeasonSelection(teamId, seasonSelection);
            setTeamName(config.teamName);
            setLocalTeamLogo(config.teamLogo);
            setTeamLogo(config.teamLogo);
            setShowSeasonConfigModal(false);
          }}
        />
      )}
    </div>
  );
};

export default TopNavBar;
